import { z, type ZodTypeAny } from 'zod';

export const variablePlaceholderSchema = z
  .object({
    $variable: z
      .object({
        name: z.string(),
        default_value: z.any().optional(),
      })
      .passthrough(),
  })
  .strict();

// `$this` is a fragment-only reference to another named resource in the same
// config. The backend resolves {"$this": "arm-1"} to the string "arm-1"
// (optionally prefixed for nested fragments). See
// data/fragment_variable_substitution.go:63-76.
export const thisReferenceSchema = z
  .object({
    $this: z.string().min(1),
  })
  .strict();

// allowVariablePlaceholders is a helper for object schemas
// it will amend the schema to allow for variable placeholders on all fields
// except for the fields specified in the omitFields array.
export const allowVariablePlaceholders = <
  T extends z.ZodObject<Record<string, ZodTypeAny>>,
>(
  schema: T,
  allowVariables = false,
  omitFields: (keyof z.infer<T>)[] = []
): T => {
  if (!allowVariables) {
    return schema; // Return unchanged if variables are not allowed
  }

  const shape = schema.shape;
  const newShape: Record<string, ZodTypeAny> = {};

  // Loop through each key in the schema and modify
  for (const key of Object.keys(shape)) {
    const fieldSchema = shape[key];
    if (fieldSchema) {
      newShape[key] = omitFields.includes(key)
        ? fieldSchema // Keep omitted fields unchanged
        : fieldSchema.or(variablePlaceholderSchema); // Add variable placeholder to all other fields
    }
  }

  // Return the new object schema without any modifiers
  // The caller will apply .partial(), .passthrough(), .strict() etc. as needed
  return z.object(newShape) as T;
};
